import type { CSSProperties } from 'react';
import {
  captureElementHierarchy,
  captureElementLayoutSnapshot,
  findFirstClippingAncestorBelow,
} from '@/hooks/telemetry/dom-layout-snapshot';
import {
  CSCROLLABLE_DEBUG_HIERARCHY_DEPTH,
  CSCROLLABLE_PADDING_MATCH_TOLERANCE_PX,
  CSCROLLABLE_WEBKIT_MASK_IMAGE_PROPERTY,
  CSS_MASK_NONE,
  CscrollableDebugReason,
  CscrollableFadeZonePosition,
  SCROLL_DIRECTION,
} from './Cscrollable.constants';
import type { ScrollDirection } from './Cscrollable.types';
import { getScrollProgressBucket, roundPxValue } from './scrollMask';

interface ScrollDebugMeasurementInput {
  element: HTMLDivElement;
  direction: ScrollDirection;
  reason: CscrollableDebugReason;
  maskImage: string;
  showStartFade: boolean;
  showEndFade: boolean;
  fadeSize: number;
  horizontalFadeSize?: number;
  verticalFadeTopSize: number;
  verticalFadeBottomSize: number;
  verticalFadeMiddleOpacity: number;
  firstItemSpacing: CSSProperties['paddingTop'];
}

interface FadeDiagnosticInput {
  element: HTMLDivElement;
  direction: ScrollDirection;
  showStartFade: boolean;
  showEndFade: boolean;
  fadeSize: number;
  verticalFadeTopSize: number;
  verticalFadeBottomSize: number;
  verticalFadeMiddleOpacity: number;
  firstItemSpacing: CSSProperties['paddingTop'];
  lastChildRect: DOMRect | null;
  elementRect: DOMRect;
}

interface ScrollAxisMetrics {
  scrollPosition: number;
  scrollSize: number;
  clientSize: number;
  maxScroll: number;
}

interface ScrollChildLayout {
  firstChildElement: HTMLElement | null;
  lastChildElement: HTMLElement | null;
  firstChildRect: DOMRect | null;
  lastChildRect: DOMRect | null;
}

function getHtmlElement(element: Element | null): HTMLElement | null {
  return element instanceof HTMLElement ? element : null;
}

function parseCssPxValue({ value }: { value: string }): number {
  return parseFloat(value) || 0;
}

function getElementVerticalPaddingDiagnostic({
  computedStyle,
}: {
  computedStyle: CSSStyleDeclaration;
}): {
  computedPaddingTopPx: number;
  computedPaddingBottomPx: number;
} {
  return {
    computedPaddingTopPx: parseCssPxValue({ value: computedStyle.paddingTop }),
    computedPaddingBottomPx: parseCssPxValue({ value: computedStyle.paddingBottom }),
  };
}

function getBottomFadeRegionSize({
  direction,
  fadeSize,
  verticalFadeBottomSize,
}: {
  direction: ScrollDirection;
  fadeSize: number;
  verticalFadeBottomSize: number;
}): number {
  if (direction === SCROLL_DIRECTION.Vertical) {
    return verticalFadeBottomSize;
  }

  return fadeSize;
}

function getExpectedBottomPaddingPx({
  direction,
  fadeSize,
}: {
  direction: ScrollDirection;
  fadeSize: number;
}): number {
  if (direction === SCROLL_DIRECTION.Vertical) {
    return fadeSize;
  }

  return 0;
}

function getPaddingBottomMatchesFadeRegion({
  computedPaddingBottomPx,
  direction,
  fadeSize,
  verticalFadeBottomSize,
}: {
  computedPaddingBottomPx: number;
  direction: ScrollDirection;
  fadeSize: number;
  verticalFadeBottomSize: number;
}): boolean {
  const bottomFadeRegionSize = getBottomFadeRegionSize({
    direction,
    fadeSize,
    verticalFadeBottomSize,
  });

  return Math.abs(computedPaddingBottomPx - bottomFadeRegionSize) < CSCROLLABLE_PADDING_MATCH_TOLERANCE_PX;
}

function getLastChildBottomVsClientBottom({
  elementClientBottom,
  lastChildRect,
}: {
  elementClientBottom: number;
  lastChildRect: DOMRect | null;
}): number | null {
  if (!lastChildRect) {
    return null;
  }

  return roundPxValue(lastChildRect.bottom - elementClientBottom);
}

function getLastChildRectBottom({ lastChildRect }: { lastChildRect: DOMRect | null }): number | null {
  if (!lastChildRect) {
    return null;
  }

  return roundPxValue(lastChildRect.bottom);
}

function getLastChildPositionInFadeZone({
  fadeRegionStartFromClientBottom,
  lastChildBottomVsClientBottom,
}: {
  fadeRegionStartFromClientBottom: number;
  lastChildBottomVsClientBottom: number | null;
}): CscrollableFadeZonePosition | null {
  if (lastChildBottomVsClientBottom == null) {
    return null;
  }

  if (lastChildBottomVsClientBottom > 0) {
    return CscrollableFadeZonePosition.BelowVisible;
  }

  if (lastChildBottomVsClientBottom > -fadeRegionStartFromClientBottom) {
    return CscrollableFadeZonePosition.InFadeZone;
  }

  return CscrollableFadeZonePosition.AboveFade;
}

function getFirstItemSpacingDiagnostic({
  firstItemSpacing,
}: {
  firstItemSpacing: CSSProperties['paddingTop'];
}): CSSProperties['paddingTop'] | null {
  if (typeof firstItemSpacing === 'number' || typeof firstItemSpacing === 'string') {
    return firstItemSpacing;
  }

  return null;
}

function getScrollAxisMetrics({
  element,
  direction,
}: {
  element: HTMLDivElement;
  direction: ScrollDirection;
}): ScrollAxisMetrics {
  if (direction === SCROLL_DIRECTION.Vertical) {
    const { scrollTop, scrollHeight, clientHeight } = element;
    return {
      scrollPosition: scrollTop,
      scrollSize: scrollHeight,
      clientSize: clientHeight,
      maxScroll: Math.max(0, scrollHeight - clientHeight),
    };
  }

  const { scrollLeft, scrollWidth, clientWidth } = element;
  return {
    scrollPosition: scrollLeft,
    scrollSize: scrollWidth,
    clientSize: clientWidth,
    maxScroll: Math.max(0, scrollWidth - clientWidth),
  };
}

function hasMaskValue({ maskImage }: { maskImage: string }): boolean {
  return maskImage.length > 0 && maskImage !== CSS_MASK_NONE;
}

function buildComputedMaskDiagnostic({
  computedStyle,
}: {
  computedStyle: CSSStyleDeclaration;
}): {
  computedMaskImage: string;
  computedWebkitMaskImage: string;
  hasComputedMask: boolean;
} {
  const computedMaskImage = computedStyle.maskImage;
  const computedWebkitMaskImage = computedStyle.getPropertyValue(CSCROLLABLE_WEBKIT_MASK_IMAGE_PROPERTY);

  return {
    computedMaskImage,
    computedWebkitMaskImage,
    hasComputedMask:
      hasMaskValue({ maskImage: computedMaskImage }) ||
      hasMaskValue({ maskImage: computedWebkitMaskImage }),
  };
}

function getScrollChildLayout({
  element,
}: {
  element: HTMLDivElement;
}): ScrollChildLayout {
  const firstChildElement = getHtmlElement(element.firstElementChild);
  const lastChildElement = getHtmlElement(element.lastElementChild);

  return {
    firstChildElement,
    lastChildElement,
    firstChildRect: firstChildElement?.getBoundingClientRect() ?? null,
    lastChildRect: lastChildElement?.getBoundingClientRect() ?? null,
  };
}

function getContentTopClipPx({
  elementRect,
  firstChildRect,
}: {
  elementRect: DOMRect;
  firstChildRect: DOMRect | null;
}): number | null {
  if (!firstChildRect) {
    return null;
  }

  return Math.max(0, roundPxValue(elementRect.top - firstChildRect.top));
}

function getContentBottomClipPx({
  elementRect,
  lastChildRect,
}: {
  elementRect: DOMRect;
  lastChildRect: DOMRect | null;
}): number | null {
  if (!lastChildRect) {
    return null;
  }

  return Math.max(0, roundPxValue(lastChildRect.bottom - elementRect.bottom));
}

function buildContentClipDiagnostic({
  childLayout,
  elementRect,
}: {
  childLayout: ScrollChildLayout;
  elementRect: DOMRect;
}): {
  contentTopClipPx: number | null;
  contentBottomClipPx: number | null;
  firstChildTopRelativeToRoot: number | null;
  lastChildBottomRelativeToRoot: number | null;
} {
  return {
    contentTopClipPx: getContentTopClipPx({
      elementRect,
      firstChildRect: childLayout.firstChildRect,
    }),
    contentBottomClipPx: getContentBottomClipPx({
      elementRect,
      lastChildRect: childLayout.lastChildRect,
    }),
    firstChildTopRelativeToRoot: childLayout.firstChildRect
      ? roundPxValue(childLayout.firstChildRect.top - elementRect.top)
      : null,
    lastChildBottomRelativeToRoot: childLayout.lastChildRect
      ? roundPxValue(childLayout.lastChildRect.bottom - elementRect.top)
      : null,
  };
}

function buildFadeDiagnostic({
  element,
  direction,
  showStartFade,
  showEndFade,
  fadeSize,
  verticalFadeTopSize,
  verticalFadeBottomSize,
  verticalFadeMiddleOpacity,
  firstItemSpacing,
  lastChildRect,
  elementRect,
}: FadeDiagnosticInput): Record<string, unknown> {
  const computedStyle = window.getComputedStyle(element);
  const { computedPaddingBottomPx, computedPaddingTopPx } = getElementVerticalPaddingDiagnostic({
    computedStyle,
  });
  const elementClientBottom = elementRect.top + element.clientHeight;
  const lastChildBottomVsClientBottom = getLastChildBottomVsClientBottom({
    elementClientBottom,
    lastChildRect,
  });
  const fadeRegionStartFromClientBottom = getBottomFadeRegionSize({
    direction,
    fadeSize,
    verticalFadeBottomSize,
  });

  return {
    direction,
    showStartFade,
    showEndFade,
    fadeSize,
    verticalFadeTopSize,
    verticalFadeBottomSize,
    verticalFadeMiddleOpacity,
    firstItemSpacing: getFirstItemSpacingDiagnostic({ firstItemSpacing }),
    computedPaddingTopPx,
    computedPaddingBottomPx,
    expectedBottomPaddingPx: getExpectedBottomPaddingPx({ direction, fadeSize }),
    paddingBottomMatchesFadeRegion: getPaddingBottomMatchesFadeRegion({
      computedPaddingBottomPx,
      direction,
      fadeSize,
      verticalFadeBottomSize,
    }),
    bottomFadeActive: showEndFade,
    bottomFadeRegionPx: showEndFade ? fadeRegionStartFromClientBottom : 0,
    elementClientBottom: roundPxValue(elementClientBottom),
    elementRectBottom: roundPxValue(elementRect.bottom),
    lastChildRectBottom: getLastChildRectBottom({ lastChildRect }),
    lastChildBottomVsClientBottom,
    lastChildPositionInFadeZone: getLastChildPositionInFadeZone({
      fadeRegionStartFromClientBottom,
      lastChildBottomVsClientBottom,
    }),
  };
}

function buildVisualDiagnostics({
  element,
  computedStyle,
  childLayout,
  elementRect,
}: {
  element: HTMLDivElement;
  computedStyle: CSSStyleDeclaration;
  childLayout: ScrollChildLayout;
  elementRect: DOMRect;
}): Record<string, unknown> {
  return {
    ...buildComputedMaskDiagnostic({ computedStyle }),
    computedOverflowX: computedStyle.overflowX,
    computedOverflowY: computedStyle.overflowY,
    computedContain: computedStyle.contain,
    computedTransform: computedStyle.transform,
    computedWillChange: computedStyle.willChange,
    ...buildContentClipDiagnostic({ childLayout, elementRect }),
    clippingAncestor: findFirstClippingAncestorBelow({ scrollable: element }),
  };
}

function buildChildSnapshots({
  element,
  childLayout,
}: {
  element: HTMLDivElement;
  childLayout: ScrollChildLayout;
}): Record<string, unknown> {
  return {
    root: captureElementLayoutSnapshot({ element, label: 'scrollable-root' }),
    firstChild: captureElementLayoutSnapshot({
      element: childLayout.firstChildElement,
      label: 'first-child',
    }),
    lastChild: captureElementLayoutSnapshot({
      element: childLayout.lastChildElement,
      label: 'last-child',
    }),
    hierarchy: captureElementHierarchy({
      element,
      label: 'scrollable-hierarchy',
      maxDepth: CSCROLLABLE_DEBUG_HIERARCHY_DEPTH,
    }),
  };
}

export function buildScrollDebugMeasurement({
  element,
  direction,
  reason,
  maskImage,
  showStartFade,
  showEndFade,
  fadeSize,
  horizontalFadeSize = 0,
  verticalFadeTopSize,
  verticalFadeBottomSize,
  verticalFadeMiddleOpacity,
  firstItemSpacing,
}: ScrollDebugMeasurementInput): Record<string, unknown> {
  const scrollMetrics = getScrollAxisMetrics({ element, direction });
  const computedStyle = window.getComputedStyle(element);
  const elementRect = element.getBoundingClientRect();
  const childLayout = getScrollChildLayout({ element });

  return {
    reason,
    direction,
    showStartFade,
    showEndFade,
    fadeSize,
    horizontalFadeSize,
    configuredMaskImage: maskImage,
    ...buildVisualDiagnostics({ element, computedStyle, childLayout, elementRect }),
    scrollPosition: scrollMetrics.scrollPosition,
    scrollSize: scrollMetrics.scrollSize,
    clientSize: scrollMetrics.clientSize,
    maxScroll: scrollMetrics.maxScroll,
    hasScrollableOverflow: scrollMetrics.maxScroll > 0,
    scrollProgressBucket: getScrollProgressBucket(scrollMetrics),
    fadeDiagnostic: buildFadeDiagnostic({
      element,
      direction,
      showStartFade,
      showEndFade,
      fadeSize,
      verticalFadeTopSize,
      verticalFadeBottomSize,
      verticalFadeMiddleOpacity,
      firstItemSpacing,
      lastChildRect: childLayout.lastChildRect,
      elementRect,
    }),
    ...buildChildSnapshots({ element, childLayout }),
  };
}
